import React, { useState, useEffect } from 'react';
import apiService from '../../services/ApiServices';
import {
  Container, Row, Col, Card, Button, Table, Form, Modal,
  Spinner, Alert, Badge
} from 'react-bootstrap';
import {
  FaPlus, FaEdit, FaTrash, FaArrowLeft, FaExchangeAlt, FaSave, FaTimes
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const AddQuestionTypeMapping = () => {
  const navigate = useNavigate();
  const [mappings, setMappings] = useState([]);
  const [testNames, setTestNames] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [formData, setFormData] = useState({
    test_type_id: '',
    question_type_master_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [mapRes, testsRes, qtRes] = await Promise.all([
        apiService.get('/api/question-type-mappings'),
        apiService.get('/api/test-names'),
        apiService.get('/api/question-type-masters')
      ]);

      // Handle Mappings
      if (mapRes?.success) {
        const data = mapRes.message?.questionTypeMappings || mapRes.message?.data || [];
        setMappings(Array.isArray(data) ? data : []);
      }

      // Handle Test Names
      if (testsRes?.success) {
        const data = testsRes.message?.testNames || testsRes.message?.data || [];
        setTestNames(Array.isArray(data) ? data : []);
      }

      // Handle Question Types
      if (qtRes?.success) {
        const data = qtRes.message?.questionTypeMasters || qtRes.message?.questionTypes || [];
        setQuestionTypes(Array.isArray(data) ? data : []);
      }

    } catch (err) {
      console.error(err);
      setError('Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.test_type_id || !formData.question_type_master_id) {
      setError('Please select both Test Name and Question Type.');
      return;
    }

    try {
      setError(null);
      const payload = {
        test_type_id: Number(formData.test_type_id),
        question_type_master_id: Number(formData.question_type_master_id)
      };

      let response;
      if (isEditMode && currentId) {
        response = await apiService.put(`/api/question-type-mappings/${currentId}`, payload);
      } else {
        response = await apiService.post('/api/question-type-mappings', payload);
      }

      if (response?.success) {
        setSuccessMsg(isEditMode ? 'Mapping updated successfully' : 'Mapping created successfully');
        setShowModal(false);
        fetchData();
      } else {
        setError(response?.message || 'Operation failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving data');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) return;
    
    try {
      const response = await apiService.delete(`/api/question-type-mappings/${id}`);
      if (response?.success) {
        setSuccessMsg('Mapping deleted successfully');
        fetchData();
      } else {
        setError(response?.message || 'Failed to delete mapping');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting mapping');
    }
  };

  const openModal = (item = null) => {
    if (item) {
      setIsEditMode(true);
      // Adjust ID field based on actual API response structure for mapping ID
      setCurrentId(item.id || item.question_type_mapping_id); 
      setFormData({
        test_type_id: item.test_type_id || '',
        question_type_master_id: item.question_type_master_id || ''
      });
    } else {
      setIsEditMode(false);
      setCurrentId(null);
      setFormData({
        test_type_id: '',
        question_type_master_id: ''
      });
    }
    setShowModal(true);
  };

  // Helper to get names for display in table
  const getTestName = (id) => testNames.find(t => (t.test_name_id || t.id) === id)?.test_name || id;
  const getQuestionTypeName = (id) => questionTypes.find(q => (q.id || q.question_type_master_id) === id)?.name || id;

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button variant="outline-secondary" className="mb-2" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" /> Back
          </Button>
          <h2 className="mb-0">
            <FaExchangeAlt className="me-2 text-primary" />
            Question Type Mapping
          </h2>
        </div>
        <Button variant="primary" onClick={() => openModal()}>
          <FaPlus className="me-2" /> Add Mapping
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <Table responsive hover className="align-middle">
              <thead className="bg-light">
                <tr>
                  <th>#</th>
                  <th>Test Name</th>
                  <th>Question Type</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {mappings.length === 0 ? (
                  <tr><td colSpan="4" className="text-center py-4">No mappings found.</td></tr>
                ) : (
                  mappings.map((item, index) => (
                    <tr key={item.id || item.question_type_mapping_id || index}>
                      <td>{index + 1}</td>
                      <td><strong>{getTestName(item.test_type_id)}</strong></td>
                      <td><Badge bg="info">{getQuestionTypeName(item.question_type_master_id)}</Badge></td>
                      <td className="text-end">
                        <Button variant="outline-primary" size="sm" className="me-2" onClick={() => openModal(item)}>
                          <FaEdit />
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => handleDelete(item.id || item.question_type_mapping_id)}>
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? 'Edit Mapping' : 'Create Mapping'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Test Name *</Form.Label>
              <Form.Select
                value={formData.test_type_id}
                onChange={(e) => setFormData({ ...formData, test_type_id: e.target.value })}
              >
                <option value="">Select Test Name</option>
                {testNames.map(t => (
                  <option key={t.test_name_id || t.id} value={t.test_name_id || t.id}>{t.test_name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Question Type *</Form.Label>
              <Form.Select
                value={formData.question_type_master_id}
                onChange={(e) => setFormData({ ...formData, question_type_master_id: e.target.value })}
              >
                <option value="">Select Question Type</option>
                {questionTypes.map(q => (
                  <option key={q.id || q.question_type_master_id} value={q.id || q.question_type_master_id}>{q.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            <FaTimes className="me-2" /> Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            <FaSave className="me-2" />
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AddQuestionTypeMapping;